import { BrandLogo } from '@/components/layout/BrandLogo'
import { useHospitalSettings } from '@/services/httpServices/queries/useWebsite'

type SiteLogoProps = {
  /** Rendered height in pixels. The uploaded logo scales to this height. */
  height?: number
  /** Colour of the "FAST AID" wordmark (used when no logo is uploaded). */
  textColor?: string
  /** Colour of the fallback medical-cross icon (used when no logo is uploaded). */
  iconColor?: string
  className?: string
  /** Extra vertical space between the FAST AID wordmark and HOSPITAL tagline. */
  taglineGap?: number
}

/**
 * Site-wide brand logo.
 *
 * Uses the hospital logo uploaded in the dashboard's Settings → General page
 * (`hospital_logo`, exposed by the public settings API) as the logo icon. The
 * "FAST AID" wordmark and "HOSPITAL" tagline are always rendered alongside it so
 * the brand text is never lost. When no logo is configured, the bundled inline
 * medical-cross icon is used as a fallback.
 */
export function SiteLogo({
  height = 42,
  textColor = '#0c193d',
  iconColor = '#055f59',
  className,
  taglineGap,
}: SiteLogoProps) {
  const { logo } = useHospitalSettings()

  return (
    <BrandLogo
      height={height}
      textColor={textColor}
      iconColor={iconColor}
      className={className}
      iconSrc={logo || undefined}
      taglineGap={taglineGap}
    />
  )
}

/**
 * Logo used by the page preloader. Uses the uploaded hospital logo when
 * configured, otherwise the bundled `loader.svg` icon.
 */
export function SiteLoaderLogo() {
  const { logo, name } = useHospitalSettings()

  if (logo) {
    return <img src={logo} alt={name} className="site-loader-logo" />
  }

  return <img src="/assets/images/loader.svg" alt="loader image" />
}
